/*
 * Copyright (c) 2021 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Tuesday, December 21st 2021, 7:19:58 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { round } from "lodash";
import { useUserContext, SessionExpiredNotification } from "authentication";
import { useSessionPingAPI } from "authentication/services/AuthAPI";

// Inspired by django session_security module and https://stackoverflow.com/questions/38774332/session-timeout-warning-modal-using-react

export const SessionMonitor = () => {
  const [sessionIsIdle, setSessionIsIdle] = React.useState(false);
  const [sessionIsExpired, setSessionIsExpired] = React.useState(false);

  const { pingServer } = useSessionPingAPI();

  const sessionWarningAfter =
    (localStorage.getItem("session_warning_timeout_ms") &&
      parseInt(localStorage.getItem("session_warning_timeout_ms"))) ??
    1500000;

  const sessionExpiryAfter =
    (localStorage.getItem("session_expiry_timeout_ms") &&
      parseInt(localStorage.getItem("session_expiry_timeout_ms"))) ??
    1800000;

  const timeToExpire = () => {
    const timeDifference = sessionExpiryAfter - sessionWarningAfter;
    if (timeDifference / 60000 > 1) return `${round(timeDifference / 60000, 2)} minutes`;
    else return `${round(timeDifference / 1000, 2)} seconds`;
  };

  const events = ["mousemove", "click", "keypress"];

  let warningTimeout: NodeJS.Timeout = null;
  let expiryTimeout: NodeJS.Timeout = null;

  const { logoutUser, setSessionIsAuthenticated } = useUserContext();

  const keepSessionAlive = () => {
    setSessionIsIdle(false);
    setSessionIsExpired(false);
    setSessionIsAuthenticated(true);
    pingServer();
  };

  const onWarningTimeout = () => setSessionIsIdle(true);

  const onSessionExpired = () => {
    setSessionIsExpired(true);
    setSessionIsAuthenticated(false);
  };

  /**
   * @method sessionTimeout
   * This function is called with each event listener to set a timeout or clear a timeout.
   */
  const sessionTimeout = () => {
    warningTimeout && clearTimeout(warningTimeout);
    expiryTimeout && clearTimeout(expiryTimeout);

    warningTimeout = setTimeout(onWarningTimeout, sessionWarningAfter); // show session warning modal.
    expiryTimeout = setTimeout(onSessionExpired, sessionExpiryAfter); // show login from on session expire.
  };

  React.useEffect(() => {
    sessionTimeout(); // Start counting after the first mount.

    for (let index in events) {
      window.addEventListener(events[index], sessionTimeout);
    }

    return () => {
      for (let index in events) {
        window.removeEventListener(events[index], sessionTimeout);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {sessionIsExpired ? (
        <SessionExpiredNotification onRelogin={keepSessionAlive} />
      ) : (
        <Dialog
          open={sessionIsIdle}
          onClose={keepSessionAlive}
          aria-labelledby="session-expire-dialog-title"
          aria-describedby="session-expire-dialog-description"
        >
          <DialogTitle id="session-expire-dialog-title">Are you still there?</DialogTitle>
          <DialogContent>
            <DialogContentText id="session-expire-dialog-description">
              Your Session is about to expire in {`${timeToExpire()}`} due to inactivity. Click anywhere on the screen
              or press any key on your keyboard to let us know you are still there.
            </DialogContentText>
          </DialogContent>
          <DialogActions className="flex-center">
            <Button size="large" onClick={keepSessionAlive} variant="text">
              Yes, I am still here
            </Button>
            <Button size="large" onClick={logoutUser} variant="text">
              Log me out
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};
